import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  RefreshCw,
  Trash2,
  Ban,
  CheckCircle,
  Calendar,
  MapPin,
  FileText,
  Truck
} from 'lucide-react';
import { getVehicle, fetchChallansForVehicle, blockVehicle, deleteVehicle } from '../services/api';
import { Vehicle, Challan } from '../types';
import clsx from 'clsx';

const parseDate = (val: any): Date | null => {
  if (!val) return null;
  if (val._seconds) return new Date(val._seconds * 1000);
  if (val.seconds) return new Date(val.seconds * 1000);
  const d = new Date(val);
  return isNaN(d.getTime()) ? null : d;
};

const formatDate = (val: any, fmt: string = 'short'): string => {
  const d = parseDate(val);
  if (!d) return '-';
  if (fmt === 'long') return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

export default function VehicleDetail() {
  const { vehicleNumber } = useParams<{ vehicleNumber: string }>();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState<(Vehicle & { challans: Challan[] }) | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async () => {
    if (!vehicleNumber) return;
    try {
      setLoading(true);
      const res = await getVehicle(vehicleNumber);
      if (res.success && res.data) setVehicle(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [vehicleNumber]);

  const handleRefresh = async () => {
    if (!vehicleNumber) return;
    setRefreshing(true);
    try {
      await fetchChallansForVehicle(vehicleNumber);
      await fetchData();
      alert('Challans refreshed');
    } catch (err: any) {
      alert(err.message);
    } finally {
      setRefreshing(false);
    }
  };

  const handleBlock = async () => {
    if (!vehicleNumber || !vehicle) return;
    const isBlocked = vehicle.compliance?.status === 'blocked';
    if (!isBlocked && !confirm('Block this vehicle?')) return;
    try {
      await blockVehicle(vehicleNumber, !isBlocked, isBlocked ? '' : 'Manual block');
      await fetchData();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleDelete = async () => {
    if (!vehicleNumber || !confirm('Delete this vehicle?')) return;
    try {
      await deleteVehicle(vehicleNumber);
      navigate('/vehicles');
    } catch (err: any) {
      alert(err.message);
    }
  };

  const statusBadge = (status: string) => {
    const styles: Record<string, string> = {
      PENDING: 'bg-yellow-100 text-yellow-700',
      PAID: 'bg-green-100 text-green-700',
      DISPOSED: 'bg-blue-100 text-blue-700',
    };
    return <span className={clsx('px-2 py-0.5 rounded text-xs font-medium', styles[status] || 'bg-gray-100 text-gray-700')}>{status}</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="text-center py-12">
        <Truck className="w-12 h-12 mx-auto text-gray-300 mb-4" />
        <h3 className="text-lg font-medium text-gray-900">Vehicle not found</h3>
        <Link to="/vehicles" className="text-primary-600 hover:underline mt-2 inline-block">Back to vehicles</Link>
      </div>
    );
  }

  const isBlocked = vehicle.compliance?.status === 'blocked';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/vehicles')} className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{vehicle.vehicleNumber}</h2>
            <p className="text-sm text-gray-500">{vehicle.transporterName || 'No transporter'}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={handleRefresh} disabled={refreshing} className="btn btn-secondary flex items-center gap-2">
            <RefreshCw className={clsx('w-4 h-4', refreshing && 'animate-spin')} />
            Refresh
          </button>
          <button onClick={handleBlock} className={clsx('btn flex items-center gap-2', isBlocked ? 'btn-success' : 'btn-warning')}>
            {isBlocked ? <CheckCircle className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
            {isBlocked ? 'Unblock' : 'Block'}
          </button>
          <button onClick={handleDelete} className="btn btn-danger flex items-center gap-2">
            <Trash2 className="w-4 h-4" /> Delete
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="card p-6 space-y-4">
          <h3 className="font-semibold text-gray-900">Vehicle Info</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-gray-500">Status</span>
              <span className={clsx('px-2 py-0.5 rounded text-xs font-medium', 
                vehicle.compliance?.status === 'clean' ? 'bg-green-100 text-green-700' :
                vehicle.compliance?.status === 'blocked' ? 'bg-red-100 text-red-700' :
                vehicle.compliance?.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'
              )}>{vehicle.compliance?.status?.toUpperCase() || 'UNKNOWN'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-500">Pending Challans</span>
              <span className="text-sm font-semibold text-warning-600">{vehicle.challanSummary?.pendingChallans || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-500">Amount Due</span>
              <span className="text-sm font-semibold text-danger-600">₹{(vehicle.challanSummary?.totalAmountPending || 0).toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-500">Added</span>
              <span className="text-sm font-medium">{formatDate(vehicle.createdAt)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-500">Last Fetch</span>
              <span className="text-sm font-medium">{formatDate(vehicle.challanSummary?.lastFetchTime, 'long')}</span>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 card p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Challans ({vehicle.challans?.length || 0})</h3>
          {!vehicle.challans?.length ? (
            <div className="text-center py-8">
              <FileText className="w-10 h-10 mx-auto text-gray-300 mb-2" />
              <p className="text-gray-500">No challans found</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {vehicle.challans.map((challan) => (
                <div key={challan.challanNumber} className="border rounded-lg p-3 hover:bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-sm">{challan.challanNumber}</p>
                      <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(challan.challanDate)}
                        {challan.challanPlace && (
                          <>
                            <MapPin className="w-3 h-3 ml-2" />
                            {challan.challanPlace}
                          </>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {challan.offences?.map(o => o.offenceDescription).join(', ')}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">₹{challan.challanAmount?.toLocaleString()}</p>
                      {statusBadge(challan.challanStatus)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
